import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useImportWizard } from '../../import/ImportWizardContext'
import { IMPORT_STEPS, KEY_LISTS, TRANSACTION_GROUPS, iconFor } from './steps'

/* Step 1, ported from "prototypes/BatchBooks Import - Select Type".

   The cards are driven entirely by GET /api/import/entities: the Lists /
   Transactions toggle is the contract's `isTransaction` flag, and the labels
   come from `label`. Nothing about the entity list is hardcoded except the
   Key List names and the icons, neither of which the contract carries.

   `canImport` is filtered here rather than server-side because Export reads the
   same endpoint and *does* offer everything in it. Bundles appear in Export and
   not here — the designs show this screen with seven lists to Export's eight —
   and the reason is real rather than cosmetic: a bundle needs its component
   items, which this import cannot collect.

   The prototype's JS references a `view-transactions` block that is missing
   from its markup, so the Transactions tab had no design. Since both tabs are
   the same card grid over the same data, it reuses the Lists layout. */
export function ImportSelectTypePage() {
  const wizard = useImportWizard()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'lists' | 'transactions'>(
    wizard.entity?.isTransaction ? 'transactions' : 'lists',
  )

  const importable = wizard.entities.filter((entity) => entity.canImport)
  const inTab = importable.filter((entity) =>
    tab === 'transactions' ? entity.isTransaction : !entity.isTransaction,
  )

  let groups: Array<{ heading: string; items: typeof inTab }>
  if (tab === 'lists') {
    groups = [
      { heading: 'Key List', items: inTab.filter((e) => KEY_LISTS.includes(e.name)) },
      { heading: 'Other List', items: inTab.filter((e) => !KEY_LISTS.includes(e.name)) },
    ]
  } else {
    const named = new Set(TRANSACTION_GROUPS.flatMap((g) => g.names))
    groups = [
      ...TRANSACTION_GROUPS.map((group) => ({
        heading: group.heading,
        items: inTab.filter((e) => group.names.includes(e.name)),
      })),
      // Anything the API adds that isn't categorised still gets shown.
      { heading: 'Other', items: inTab.filter((e) => !named.has(e.name)) },
    ]
  }

  const choose = (name: string) => {
    wizard.setEntityType(name)
    navigate('/app/import/upload')
  }

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={IMPORT_STEPS} current={0} />

      <h2>Select Import Type</h2>

      <div className="bb-radios" role="radiogroup" aria-label="Import category">
        <label className="bb-radio">
          <input
            type="radio"
            name="import_type"
            value="lists"
            checked={tab === 'lists'}
            onChange={() => setTab('lists')}
          />
          Lists
        </label>
        <label className="bb-radio">
          <input
            type="radio"
            name="import_type"
            value="transactions"
            checked={tab === 'transactions'}
            onChange={() => setTab('transactions')}
          />
          Transactions
        </label>
      </div>

      {groups.map((group) =>
        group.items.length === 0 ? null : (
          <div className="bb-group" key={group.heading}>
            <h3 className="bb-group-h">{group.heading}</h3>
            <div className="bb-types">
              {group.items.map((entity) => (
                <button
                  className={entity.name === wizard.entityType ? 'bb-type bb-type-on' : 'bb-type'}
                  type="button"
                  key={entity.name}
                  aria-pressed={entity.name === wizard.entityType}
                  onClick={() => choose(entity.name)}
                >
                  <span className="bb-type-ico">
                    <BbIcon name={iconFor(entity.name)} />
                  </span>
                  {entity.label ?? entity.name}
                </button>
              ))}
            </div>
          </div>
        ),
      )}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app">
          Cancel
        </Link>
        {wizard.entityType ? (
          <Link className="bb-btn bb-btn-green" to="/app/import/upload">
            Continue with {wizard.entity?.label ?? wizard.entityType}
            <BbIcon name="arrow-right" />
          </Link>
        ) : null}
      </div>
    </div>
  )
}
